import { useEffect } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import VideoCard from "@/components/base/VideoCard";
import { getArticleById, getLatestArticles } from "@/mocks/articles";
import { getVideoById, getNewVideos } from "@/mocks/videos";
import ArticleCard from "@/components/base/ArticleCard";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  const article = getArticleById(id || "");
  const video = getVideoById(id || "");
  const latestArticles = getLatestArticles(3);
  const recommendVideos = getNewVideos().slice(0, 4);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // If it's a video ID
  if (video) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: video.category === "movie" ? "电影" : video.category === "tv" ? "电视剧" : video.category === "variety" ? "综艺" : "动漫", href: `/category/${video.category}` },
                { label: video.title },
              ]}
            />

            <article className="py-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1">
                  <div className="sticky top-24">
                    <div className="aspect-[3/4] rounded-xl overflow-hidden bg-neutral-100">
                      <img
                        src={video.cover}
                        alt={`${video.title} - 白云视频免费在线观看`}
                        title={`${video.title}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button className="w-full mt-4 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-semibold whitespace-nowrap transition-all flex items-center justify-center gap-2">
                      <i className="ri-play-fill text-lg"></i>
                      立即播放
                    </button>
                  </div>
                </div>

                <div className="lg:col-span-2">
                  <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "var(--font-heading)", color: "oklch(var(--foreground-900))" }}>
                    {video.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mt-4">
                    <span className="px-3 py-1 bg-accent-100 text-accent-700 text-sm font-medium rounded-full whitespace-nowrap">
                      评分 {video.score}
                    </span>
                    <span className="text-sm text-foreground-500">{video.year}</span>
                    <span className="text-sm text-foreground-500">{video.region}</span>
                    <span className="text-sm text-foreground-500">{video.episodeInfo}</span>
                  </div>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-foreground-400 whitespace-nowrap">导演：</span>
                      <span className="text-sm text-foreground-700">{video.director}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-foreground-400 whitespace-nowrap">主演：</span>
                      <span className="text-sm text-foreground-700">{video.actors}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-foreground-400 whitespace-nowrap">类型：</span>
                      <span className="text-sm text-foreground-700">
                        {video.category === "movie" ? "电影" : video.category === "tv" ? "电视剧" : video.category === "variety" ? "综艺" : "动漫"}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-sm text-foreground-400 whitespace-nowrap">地区：</span>
                      <span className="text-sm text-foreground-700">{video.region}</span>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h2 className="text-lg font-semibold mb-3" style={{ color: "oklch(var(--foreground-900))" }}>剧情简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
                  </div>

                  <div className="mt-6">
                    <h2 className="text-lg font-semibold mb-3" style={{ color: "oklch(var(--foreground-900))" }}>播放列表</h2>
                    <div className="flex flex-wrap gap-2">
                      {Array.from({ length: 8 }, (_, i) => (
                        <button
                          key={i}
                          className={`w-12 h-12 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                            i === 0
                              ? "bg-primary-500 text-white"
                              : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700"
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </article>

            {/* Related Videos */}
            <section className="py-8">
              <h2 className="text-xl font-bold mb-5" style={{ fontFamily: "var(--font-heading)", color: "oklch(var(--foreground-900))" }}>
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
                {recommendVideos.map((v) => (
                  <VideoCard key={v.id} video={v} />
                ))}
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Article view
  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
            <p className="text-foreground-500">文章不存在</p>
            <Link to="/" className="mt-4 inline-flex text-primary-600 hover:text-primary-700 text-sm font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: "影视资讯" },
                { label: article.title },
              ]}
            />

            <article className="py-6">
              <header>
                <h1 className="text-2xl md:text-3xl font-bold leading-tight" style={{ fontFamily: "var(--font-heading)", color: "oklch(var(--foreground-900))" }}>
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-4">
                  <span className="text-sm text-foreground-400">{article.source}</span>
                  <span className="text-sm text-foreground-300">·</span>
                  <span className="text-sm text-foreground-400">{article.publishedAt}</span>
                  <span className="text-sm text-foreground-300">·</span>
                  <span className="text-sm text-foreground-400">{article.author}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
              </header>

              <div className="mt-6 rounded-xl overflow-hidden">
                <img
                  src={article.cover}
                  alt={article.title}
                  className="w-full aspect-video object-cover"
                />
              </div>

              <div
                className="mt-8 prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Article",
                    headline: article.title,
                    description: article.excerpt,
                    image: article.cover,
                    datePublished: article.publishedAt,
                    author: { "@type": "Person", name: article.author },
                    publisher: { "@type": "Organization", name: "白云视频" },
                  }),
                }}
              />
            </article>

            {/* Related Articles */}
            <section className="py-8 border-t border-neutral-100">
              <h2 className="text-lg font-semibold mb-4" style={{ color: "oklch(var(--foreground-900))" }}>相关文章</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {latestArticles.filter((a) => a.id !== article.id).slice(0, 2).map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}